import React, { useEffect, useState } from 'react';
import { 
  Card, 
  Typography, 
  Box, 
  Alert, 
  CircularProgress, 
  Avatar, 
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  Chip,
  TextField,
  InputAdornment,
  Divider,
  Container,
  Paper
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import GroupIcon from '@mui/icons-material/Group';
import SearchIcon from '@mui/icons-material/Search';
import WorkIcon from '@mui/icons-material/Work';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import { apiGet } from '../api/apiClient';
import PersonalDetailsGrid from './PersonalDetailsGrid';

const Colleagues = () => {
  const [colleagues, setColleagues] = useState([]);
  const [filteredColleagues, setFilteredColleagues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [openColleague, setOpenColleague] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const filtered = colleagues.filter(colleague => {
      const searchLower = searchTerm.toLowerCase();
      
      return (
        colleague.daily_grind?.toLowerCase().includes(searchLower) ||
        colleague.about_me?.toLowerCase().includes(searchLower) ||
        colleague.fun_fact?.toLowerCase().includes(searchLower) ||
        colleague.employee?.employee_name?.toLowerCase().includes(searchLower) ||
        colleague.employee?.designation_id?.toLowerCase().includes(searchLower) ||
        colleague.employee?.department_name?.toLowerCase().includes(searchLower)
      );
    });
    setFilteredColleagues(filtered);
  }, [searchTerm, colleagues]);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    
    try {
      const colleaguesData = await apiGet('/other_info');
      setColleagues(colleaguesData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return 'C';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 ,mt: 8 }}>
      {/* Header Section */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 3, 
          mb: 3, 
          backgroundImage: 'url(/banner.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'top center',
          backgroundRepeat: 'no-repeat',
          borderRadius: 2,
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(13, 0, 74, 0.7)',
            borderRadius: 2,
            zIndex: 1
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2, position: 'relative', zIndex: 2 }}>
          <Box sx={{ textAlign: 'left' }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'white' }}>
              Team Directory
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)', fontStyle: 'italic' }}>
              Discover your colleagues and collaborate with confidence
            </Typography>
          </Box>
          
          {/* Search Bar - Right Aligned */}
          <TextField
            placeholder="Search colleagues..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ 
              width: 350,
              bgcolor: 'white',
              borderRadius: 1,
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: 'transparent',
                },
              }
            }}
          />
        </Box>
      </Paper>

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={50} sx={{ color: '#0D004A' }} />
        </Box>
      )}
      
      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Colleagues Compact Cards Grid - 4 cards per row */}
      <Grid container spacing={3} justifyContent="center">
        {filteredColleagues.map(colleague => {
          const employee = colleague.employee || {};
          const displayName = employee.employee_name || 'Colleague';
          const hasFullInfo = displayName !== 'Colleague' && employee.designation_id && employee.department_name;
          
          return (
            <Grid item xs={12} sm={6} md={3} lg={3} key={colleague.id} sx={{ display: 'flex', justifyContent: 'center' }}>
              <Card 
                sx={{ 
                  height: 280,
                  width: 280, // Fixed width of exactly 280px
                  minWidth: 280, // Ensure minimum width
                  maxWidth: 280, // Ensure maximum width
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 3,
                  boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 16px 32px rgba(0,0,0,0.2)',
                  }
                }}
                onClick={() => setOpenColleague(colleague)}
              >
                {/* Dark Blue Upper Section */}
                <Box sx={{
                  height: 100,
                  background: 'linear-gradient(135deg, #0D004A 0%, #1a0a6e 100%)',
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {/* Circular Avatar overlapping both sections */}
                  <Avatar 
                    sx={{ 
                      width: 80, 
                      height: 80, 
                      bgcolor: 'white',
                      fontSize: 28,
                      fontWeight: 'bold',
                      color: '#0D004A',
                      border: '4px solid white',
                      boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
                      position: 'absolute',
                      top: '100%', // Position at the bottom of blue section
                      left: '50%',
                      transform: 'translate(-50%, -50%)', // Center horizontally and move up by half its height
                      zIndex: 2
                    }}
                  >
                    {getInitials(displayName)}
                  </Avatar>
                </Box>

                {/* White Lower Section */}
                <Box sx={{
                  flex: 1,
                  bgcolor: 'white',
                  pt: 6, // Increased padding to account for overlapping avatar
                  pb: 3,
                  px: 2, // Reduced horizontal padding for better text wrapping
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  justifyContent: 'flex-start',
                  minHeight: 0 // Prevents flex item from growing
                }}>
                  {/* Name */}
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 700,
                      color: '#0D004A',
                      mb: 1,
                      fontSize: hasFullInfo ? '1rem' : '1.2rem',
                      lineHeight: 1.2,
                      textAlign: 'center',
                      maxWidth: '100%',
                      wordBreak: 'break-word', 
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {displayName}
                  </Typography>
                  
                  {/* Designation/Role */}
                  {employee.designation_id && (
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#666',
                        mb: 1.5,
                        fontSize: hasFullInfo ? '0.8rem' : '0.9rem', // Smaller font for cards with full info
                        fontWeight: 500,
                        textAlign: 'center',
                        wordWrap: 'break-word',
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: 'vertical'
                      }}
                    >
                      {employee.designation_id}
                    </Typography>
                  )}
                  
                  {/* Department */}
                  {employee.department_name && (
                    <Chip 
                      label={employee.department_name}
                      size="small"
                      sx={{ 
                        height: hasFullInfo ? 24 : 28, // Smaller chip for cards with full info
                        fontSize: hasFullInfo ? '0.7rem' : '0.8rem', // Smaller font for cards with full info
                        bgcolor: '#E3F2FD',
                        color: '#0D004A',
                        fontWeight: 600,
                        borderRadius: 2,
                        border: '1px solid #BBDEFB',
                        maxWidth: '100%'
                      }}
                    />
                  )}
                </Box>
                
                {/* Info Icon - Top Right Corner */}
                <IconButton 
                  size="small"
                  sx={{ 
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    color: 'white',
                    bgcolor: 'rgba(0, 0, 0, 0.3)',
                    width: 32,
                    height: 32,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                    zIndex: 3,
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.2)',
                      transform: 'scale(1.1)'
                    }
                  }}
                >
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Empty State */}
      {!loading && filteredColleagues.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <GroupIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
            {searchTerm ? 'No colleagues found' : 'No colleagues to display'}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.disabled' }}>
            {searchTerm ? 'Try adjusting your search terms' : 'Check back later for updates'}
          </Typography>
        </Box>
      )}

      {/* Detailed Profile Dialog */}
      <Dialog 
        open={!!openColleague} 
        onClose={() => setOpenColleague(null)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          background: 'linear-gradient(135deg, #0D004A 0%, #1a0a6e 100%)',
          color: 'white',
          py: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Colleague Profile
            </Typography>
          </Box>
          <IconButton 
            onClick={() => setOpenColleague(null)}
            sx={{ color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 4 }}>
          {openColleague && (
            <Box>
              {/* Profile Header */}
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 2 }}>
                <Avatar 
                  sx={{ 
                    width: 100, 
                    height: 100, 
                    mr: 3,
                    bgcolor: '#0D004A',
                    fontSize: 36,
                    fontWeight: 'bold',
                    border: '4px solid #E3F2FD',
                    color: 'white'
                  }}
                >
                  {getInitials(openColleague.employee?.employee_name || 'Colleague')}
                </Avatar>
                
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5, color: '#0D004A' }}>
                    {openColleague.employee?.employee_name || 'Colleague'}
                  </Typography>
                  
                  {openColleague.employee && (
                    <Box>
                      {openColleague.employee.designation_id && (
                        <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
                          {openColleague.employee.designation_id}
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {openColleague.employee.department_name && (
                          <Chip 
                            icon={<WorkIcon />}
                            label={openColleague.employee.department_name}
                            size="small"
                            sx={{ bgcolor: '#E3F2FD' }}
                          />
                        )}
                        {openColleague.employee.city_id && (
                          <Chip 
                            icon={<LocationOnIcon />}
                            label={openColleague.employee.city_id}
                            size="small"
                            sx={{ bgcolor: '#E8F5E9' }}
                          />
                        )}
                        {openColleague.employee.employee_code && (
                          <Chip 
                            label={`ID: ${openColleague.employee.employee_code}`}
                            size="small"
                            sx={{ bgcolor: '#FFF3E0', color: '#E65100' }}
                          />
                        )}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>

              <Divider sx={{ mb: 3 }} />

              {/* Professional Information */}
              {openColleague.employee?.reporting_to && (
                <Box sx={{ mb: 3, p: 2, bgcolor: '#F8F9FA', borderRadius: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, color: '#0D004A' }}>
                    Professional Details
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <PersonIcon sx={{ color: '#0D004A', fontSize: 20 }} />
                    <Typography variant="body2">
                      <strong>Reports to:</strong> {openColleague.employee.reporting_to}
                    </Typography>
                  </Box>
                  {openColleague.employee.department_name && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <WorkIcon sx={{ color: '#0D004A', fontSize: 20 }} />
                      <Typography variant="body2">
                        <strong>Department:</strong> {openColleague.employee.department_name}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              {/* About Section */}
              {openColleague.about_me && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: '#0D004A' }}>
                    About Me
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                    {openColleague.about_me}
                  </Typography>
                </Box>
              )}

              {openColleague.daily_grind && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: '#0D004A' }}>
                    What I Do Daily
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                    {openColleague.daily_grind}
                  </Typography>
                </Box>
              )}

               {/* Personal Details Grid */}
               <PersonalDetailsGrid colleague={openColleague} />
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default Colleagues;